import { ReactNode, RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { IsNever, useIsomorphicLayoutEffect } from '@direct-frontend/stdlib';
import { Scrim } from '@direct-frontend/components';

import { BaseFields, InferFieldsValue } from '../fields/types';
import { BaseValidationError } from '../types';

import { $internals, FormActionsMap, FormAsyncActionsMap, FormDeclaration, UnknownFormInstance } from './types';
import { useSubscription } from './useSubscription';
import { useSyncFormInitialization } from './useSyncFormInitialization';
import { getFormsContext } from './FormsContext';

/** Пропсы компонента `Form` */
type FormProps<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
    FormAsyncActions extends FormAsyncActionsMap<FormFields, FormValidationError, FormContext, FormActions> = never,
> = {
    /** Дочерние элементы, которые будут отрендерены после инициализации формы */
    children: ReactNode;
    /** Форма, которую необходимо проинициализировать */
    form: FormDeclaration<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue,
        FormAsyncActions
    >;
    /** Идентификатор формы */
    id: string;
    /** Начальное значение формы – используется только тогда, когда форма не проинициализирована никаким значением */
    initialValue?: InferFieldsValue<FormFields>;
    /**
     * Опциональный ref для области, поверх которой нужно натянуть Scrim
     */
    formContentRef?: RefObject<HTMLElement | null>;
} & IsNever<
    FormContextInput,
    {},
    {
        /** Значение контекста формы */
        context: FormContextInput;
    }
>;

/** Корневой компонент формы – инициализирует значение, управляет подписками и очищает данные формы */
export function Form<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
    FormAsyncActions extends FormAsyncActionsMap<FormFields, FormValidationError, FormContext, FormActions> = never,
>({
    children,
    form,
    id,
    initialValue,
    formContentRef,
    ...options
}: FormProps<
    FormName,
    FormFields,
    FormValidationError,
    FormContextInput,
    FormContext,
    FormActions,
    FormSubmitArg,
    FormSubmitReturnValue,
    FormAsyncActions
>) {
    const instance = useMemo(() => form.getInstance(id), [form, id]);

    const prevInstanceRef = useRef<UnknownFormInstance | null>(null);
    const { shouldDisableFormOnSubmit, shouldBlockReinitialization } = getFormsContext();

    // Поле context опционально внутри условного типа – оператор in сужает его тип до `unknown`
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
    const context = ('context' in options ? options.context : undefined) as FormContextInput;

    // Мемоизируем функцию инициализации, чтобы лишний раз не вызывать эффект внутри `useSyncFormInitialization`:
    // – Если изменится form или initialValue, то функция вызовется заново, так как мы не знаем изменилась ли форма
    // – Если это та же самая форма, то от повторной инициализации защитит проверка внутри `instance.initializeState`
    const initializeState = useCallback(() => {
        let shouldReinitialize = false;

        // Реинициализируем форму при смене её инстанса (смена form/id) или ремаунте компонента,
        // но только если в сторе уже есть её клиентское состояние, не поднятое с сервера.
        // shouldBlockReinitialization — для тестового withFormInitialization: DIRECT-327472
        if (prevInstanceRef.current !== instance) {
            prevInstanceRef.current = instance;
            shouldReinitialize =
                instance.isInitialized() && !instance[$internals].isHydratedFromServer && !shouldBlockReinitialization;
        }

        instance[$internals].initializeState(initialValue, context, {
            shouldReinitialize: shouldReinitialize,
        });
    }, [instance, initialValue, context, shouldBlockReinitialization]);

    // На сервере состояние формы записывается прямо в рендере, поэтому в разметку уезжают children.
    // Чтобы клиент на проходе гидрации дал ту же разметку, инициализируем в рендере и там –
    // но только если запись идемпотентна, см. `canInitializeDuringRender`.
    const canInitializeDuringRender = useCallback(() => instance[$internals].canInitializeDuringRender(), [instance]);

    const isInitialized = useSyncFormInitialization(initializeState, canInitializeDuringRender);

    useIsomorphicLayoutEffect(() => {
        instance[$internals].startReactions();
    }, [instance, initializeState]);

    // При изменении параметров контекста обновляем его значение
    useEffect(() => {
        instance.setContext(context);
    }, [instance, context]);

    useSubscription(instance);

    const isFormReady = instance[$internals].useIsFormInitialized() && isInitialized;

    if (!isFormReady || !isInitialized) {
        return null;
    }

    if (!shouldDisableFormOnSubmit) {
        return <>{children}</>;
    }

    return (
        <FormContentWithScrim form={form} id={id} formContentRef={formContentRef}>
            {children}
        </FormContentWithScrim>
    );
}

type FormContentWithScrimProps<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
    FormAsyncActions extends FormAsyncActionsMap<FormFields, FormValidationError, FormContext, FormActions> = never,
> = {
    children: ReactNode;
    form: FormDeclaration<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue,
        FormAsyncActions
    >;
    id: string;
    formContentRef?: RefObject<HTMLElement | null>;
};

function FormContentWithScrim<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
    FormAsyncActions extends FormAsyncActionsMap<FormFields, FormValidationError, FormContext, FormActions> = never,
>({
    children,
    form,
    id,
    formContentRef,
}: FormContentWithScrimProps<
    FormName,
    FormFields,
    FormValidationError,
    FormContextInput,
    FormContext,
    FormActions,
    FormSubmitArg,
    FormSubmitReturnValue,
    FormAsyncActions
>) {
    const instance = useMemo(() => form.getInstance(id), [form, id]);

    const isSubmitting = instance[$internals].useIsFormSubmitting();

    return (
        <Scrim isActive={isSubmitting} targetRef={formContentRef} data-testid={`Scrim.${form.name}`}>
            {children}
        </Scrim>
    );
}
